import { useState, useEffect } from 'react';
import { Save, Server, Loader2, AlertTriangle } from 'lucide-react';
import Modal from '../../common/Modal';
import edgeService from '../../../services/edge.service';
import { ipv4ToNumber, ipRangesOverlap } from '../../../utils/ip';

interface LogicalInterface {
  id: string;
  name: string;
  role: string;
  ipv4?: string;
  subnet?: string;
  description?: string;
}

interface DHCPPoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  subscription?: any;
  mode: 'add' | 'edit';
  edgeId?: string;
  /**
   * When set, pre-fills + hides the logical-interface selector. Used by the
   * embedded subscription sections inside the Logical Interface modal — the
   * parent interface's identity is already known, so the selector would just
   * be noise.
   */
  lockedInterfaceId?: string;
  /**
   * Draft mode: the parent interface doesn't exist yet — the operator is
   * composing it and this pool together for a single atomic commit. Hides the
   * interface selector (like lockedInterfaceId) and leaves logical_port_id
   * empty; the commit endpoint assigns it server-side.
   */
  draftInterface?: { name: string; role?: string; ipv4?: string; ipv4Aliases?: any[] };
  /**
   * Other DHCP pools already on this interface (live rows and unsaved drafts).
   * Used to warn/block inline when the entered range overlaps one of them,
   * mirroring the backend's assertNoDhcpPoolOverlap check. The pool being
   * edited is filtered out by matching `subscription.id`.
   */
  existingDhcpPools?: Array<{ id?: string | number; subscription_name?: string; configuration?: any }>;
}

const DHCPPoolModal = ({ isOpen, onClose, onSave, subscription, mode, edgeId, lockedInterfaceId, draftInterface, existingDhcpPools }: DHCPPoolModalProps) => {
  const isDraft = !!draftInterface && !lockedInterfaceId;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logicalInterface: '',
    startIp: '',
    endIp: '',
    subnetMask: '255.255.255.0',
    leaseTime: 86400,
    primaryDns: '8.8.8.8',
    secondaryDns: '',
    gateway: '',
  });

  const [logicalInterfaces, setLogicalInterfaces] = useState<LogicalInterface[]>([]);
  const [interfacesLoading, setInterfacesLoading] = useState(false);

  // Reset form data when subscription changes or modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Map API fields to form fields (V2 API uses snake_case and nested configuration)
    const config = subscription?.configuration || {};
    // DNS servers can be array (V2) or comma-separated string (legacy)
    const dnsServers = Array.isArray(config?.dns_servers)
      ? config.dns_servers
      : (config?.dns_server?.split(',') || []);

    setFormData({
      name: subscription?.subscription_name || subscription?.name || '',
      description: subscription?.description || '',
      logicalInterface: subscription?.logical_port_id || lockedInterfaceId || '',
      startIp: config?.start_ip || '',
      endIp: config?.end_ip || '',
      subnetMask: config?.subnet_mask || '255.255.255.0',
      leaseTime: config?.lease_time || 86400,
      primaryDns: dnsServers[0] || '8.8.8.8',
      secondaryDns: dnsServers[1] || '',
      gateway: config?.gateway || '',
    });
  }, [isOpen, subscription, lockedInterfaceId]);

  // Fetch logical interfaces when modal opens
  useEffect(() => {
    const fetchInterfaces = async () => {
      if (!isOpen || !edgeId) return;

      setInterfacesLoading(true);
      try {
        const response = await edgeService.listLogicalInterfaces(edgeId);
        setLogicalInterfaces(response.items || []);
      } catch (err) {
        console.error('Failed to fetch logical interfaces:', err);
      } finally {
        setInterfacesLoading(false);
      }
    };

    fetchInterfaces();
  }, [isOpen, edgeId]);

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Inline overlap guard: does the entered range collide with another DHCP pool
  // already on this interface? Excludes the pool being edited. Mirrors the
  // backend's assertNoDhcpPoolOverlap so the operator sees it before submitting.
  const otherPools = (existingDhcpPools || []).filter(
    (p) => subscription == null || String(p.id) !== String(subscription.id),
  );
  const overlappingPool =
    formData.startIp && formData.endIp
      ? otherPools.find((p) => {
          const c = p.configuration || {};
          return c.start_ip && c.end_ip && ipRangesOverlap(formData.startIp, formData.endIp, c.start_ip, c.end_ip);
        })
      : undefined;
  const hasOverlap = !!overlappingPool;

  // Dynamic range validity: both must be valid IPv4 and start < end (matches
  // the server's rule — a single-address pool where start === end is rejected).
  // Only evaluated once both fields have content (empty is "required").
  const rangeError: string | null = (() => {
    const s = (formData.startIp || '').trim();
    const e = (formData.endIp || '').trim();
    if (!s || !e) return null;
    const sn = ipv4ToNumber(s);
    if (sn === null) return 'Starting IP is not a valid IPv4 address.';
    const en = ipv4ToNumber(e);
    if (en === null) return 'Ending IP is not a valid IPv4 address.';
    if (sn >= en) return 'Starting IP must be lower than the Ending IP.';
    return null;
  })();

  const handleSubmit = () => {
    if (rangeError || hasOverlap) return; // blocked; the inline error explains why

    // Build DNS servers array from primary/secondary
    const dnsServers: string[] = [];
    if (formData.primaryDns) dnsServers.push(formData.primaryDns);
    if (formData.secondaryDns) dnsServers.push(formData.secondaryDns);

    // Transform to V2 API format
    const apiPayload = {
      logical_port_id: formData.logicalInterface,
      subscription_type: 'dhcp_pool',
      subscription_name: formData.name,
      description: formData.description || undefined,
      configuration: {
        start_ip: formData.startIp,
        end_ip: formData.endIp,
        subnet_mask: formData.subnetMask,
        gateway: formData.gateway || undefined,
        dns_servers: dnsServers.length > 0 ? dnsServers : undefined,
        lease_time: formData.leaseTime,
      },
    };

    onSave(apiPayload);
    onClose();
  };

  // Filter for LAN interfaces only
  const lanInterfaces = logicalInterfaces.filter((iface) => iface.role === 'LAN');

  // Get selected interface details for validation hints
  const selectedInterface = lanInterfaces.find((iface) => iface.id === formData.logicalInterface || iface.name === formData.logicalInterface);

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        disabled={hasOverlap || !!rangeError}
        title={rangeError || (hasOverlap ? 'Range overlaps another DHCP pool on this interface' : undefined)}
        className={`btn-primary inline-flex items-center space-x-2 ${hasOverlap || rangeError ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create DHCP Pool' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add DHCP Pool' : `Edit DHCP Pool: ${subscription?.name}`}
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
          <Server className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900">DHCP Server Pool</p>
            <p className="text-sm text-blue-700 mt-1">
              Automatically assign IP addresses to devices on the selected LAN interface.
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Corporate DHCP Pool"
                className="input-base w-full"
                required
              />
            </div>

            {!lockedInterfaceId && !isDraft && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logical Interface <span className="text-red-500">*</span>
                  {interfacesLoading && <Loader2 className="w-3 h-3 inline ml-2 animate-spin" />}
                </label>
                <select
                  value={formData.logicalInterface}
                  onChange={(e) => handleChange('logicalInterface', e.target.value)}
                  className="input-base w-full"
                  required
                  disabled={interfacesLoading}
                >
                  <option value="">Select LAN interface</option>
                  {lanInterfaces.map((iface) => (
                    <option key={iface.id} value={iface.id}>
                      {iface.name} - {iface.ipv4 || 'No IP'}{iface.description ? ` (${iface.description})` : ''}
                    </option>
                  ))}
                </select>
                {selectedInterface && selectedInterface.ipv4 && (
                  <p className="mt-1 text-xs text-gray-500">
                    DHCP pool must be within {selectedInterface.ipv4}/{selectedInterface.subnet || '255.255.255.0'} subnet
                  </p>
                )}
                {lanInterfaces.length === 0 && !interfacesLoading && (
                  <p className="mt-1 text-xs text-amber-600">
                    No LAN interfaces configured. Create a LAN interface first.
                  </p>
                )}
              </div>
            )}

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="e.g., Main corporate network DHCP"
                className="input-base w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Starting IP Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.startIp}
                onChange={(e) => handleChange('startIp', e.target.value)}
                placeholder="e.g., 192.168.1.10"
                className={`input-base w-full ${rangeError ? 'border-red-500' : ''}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ending IP Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.endIp}
                onChange={(e) => handleChange('endIp', e.target.value)}
                placeholder="e.g., 192.168.1.200"
                className={`input-base w-full ${hasOverlap || rangeError ? 'border-red-500' : ''}`}
                required
              />
            </div>

            {rangeError && (
              <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{rangeError}</p>
              </div>
            )}

            {!rangeError && hasOverlap && overlappingPool && (
              <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">
                  This range overlaps the existing pool{' '}
                  <strong>{overlappingPool.subscription_name || 'DHCP pool'}</strong>{' '}
                  ({overlappingPool.configuration?.start_ip}–{overlappingPool.configuration?.end_ip}) on this
                  interface. Each DHCP pool must use a distinct, non-overlapping range.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subnet Mask</label>
              <select
                value={formData.subnetMask}
                onChange={(e) => handleChange('subnetMask', e.target.value)}
                className="input-base w-full"
              >
                <option value="255.255.255.0">/24 (255.255.255.0)</option>
                <option value="255.255.254.0">/23 (255.255.254.0)</option>
                <option value="255.255.252.0">/22 (255.255.252.0)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lease Time (seconds)
              </label>
              <input
                type="number"
                value={formData.leaseTime}
                onChange={(e) => handleChange('leaseTime', parseInt(e.target.value))}
                min={300}
                max={604800}
                className="input-base w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Default: 86400 (24 hours)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary DNS <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.primaryDns}
                onChange={(e) => handleChange('primaryDns', e.target.value)}
                placeholder="e.g., 8.8.8.8"
                className="input-base w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary DNS</label>
              <input
                type="text"
                value={formData.secondaryDns}
                onChange={(e) => handleChange('secondaryDns', e.target.value)}
                placeholder="e.g., 8.8.4.4"
                className="input-base w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gateway</label>
              <input
                type="text"
                value={formData.gateway}
                onChange={(e) => handleChange('gateway', e.target.value)}
                placeholder="e.g., 192.168.1.1"
                className="input-base w-full"
              />
              <p className="mt-1 text-xs text-gray-500">Leave blank to use interface IP</p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DHCPPoolModal;

